import { useLocalSearchParams, useRouter } from "expo-router";
import {
  parseTenantActivationFilters,
  timeZoneAfterSiteChange,
  type DefaultTimeZone,
  type ParsedTenantActivationFilters,
} from "@visioni/a3s-monitoring/app";
import type { ActivationFilterValues } from "../components/ActivationFilterBar";
import { useActivationScopeOptions, type ActivationScopeOptions } from "./useActivationScopeOptions";
import { useListTimeZone } from "./useListTimeZone";

export interface TenantListFilters {
  readonly tenantId: string;
  readonly values: ActivationFilterValues;
  readonly scope: ActivationScopeOptions;
  /** The zone applied; `undefined` until the selected site's zone has loaded. */
  readonly timeZone: string | undefined;
  readonly fallback: DefaultTimeZone | undefined;
  /** `undefined` until the zone is known, since the range cannot be read without it. */
  readonly parsed: ParsedTenantActivationFilters | undefined;
  readonly onChange: (next: ActivationFilterValues) => void;
}

const blank = (value: string | undefined) => (value === "" ? undefined : value);

/**
 * The tenant activation and signal lists' filters, which live in the URL
 * (`?site=&camera=&tz=&from=&to=&order=`) so a filtered list can be shared, and are
 * the same params on both so a view moves between them by changing the path.
 */
export function useTenantListFilters({
  viewerTimeZone = false,
}: {
  /**
   * Default to the viewer's zone even with a site selected, rather than the
   * site's. For views that sum counts by day, where the viewer's days are the
   * ones being asked about.
   */
  readonly viewerTimeZone?: boolean;
} = {}): TenantListFilters {
  const router = useRouter();
  const params = useLocalSearchParams<{
    tenantId: string;
    site?: string;
    camera?: string;
    from?: string;
    to?: string;
    tz?: string;
    order?: string;
  }>();
  const values: ActivationFilterValues = {
    site: blank(params.site),
    camera: blank(params.camera),
    from: blank(params.from),
    to: blank(params.to),
    tz: blank(params.tz),
    order: blank(params.order),
  };

  const scope = useActivationScopeOptions();
  const { timeZone, fallback, defaultFor } = useListTimeZone({
    tz: values.tz,
    site: viewerTimeZone ? undefined : values.site,
    siteTimeZone: scope.siteTimeZone,
    onResolve: (tz) => router.setParams({ tz }),
  });

  const parsed = timeZone === undefined ? undefined : parseTenantActivationFilters({ ...values, tz: timeZone });

  const onChange = (next: ActivationFilterValues) => {
    const tz =
      next.site !== values.site && next.tz !== undefined ?
        timeZoneAfterSiteChange(
          next.tz,
          fallback?.timeZone,
          defaultFor(viewerTimeZone ? undefined : next.site)?.timeZone,
        )
      : next.tz;
    // Every key is always passed, so a cleared filter is removed from the URL
    // rather than left behind from the previous selection.
    router.setParams({ site: next.site, camera: next.camera, from: next.from, to: next.to, tz, order: next.order });
  };

  return { tenantId: params.tenantId, values, scope, timeZone, fallback, parsed, onChange };
}
